import { onMount } from 'solid-js';
import { parseShareIdFromSearch, resolveDashboardShare } from '../utils/dashboardUrlUtils';
import { DASHBOARD_SHARE_VERSION } from '../utils/dashboardShareRegistry';
import { consumePendingPageShareRestore, peekPendingPageShareRestore } from '../store/pageShareStateStore';
import { useDashboardPageShareMirror } from './useDashboardPageShareMirror';
import { warn } from '../utils/console';

const restoreByPage = new Map<string, Promise<boolean>>();

export function willRestoreShare(pageKey: string): boolean {
  return Boolean(parseShareIdFromSearch()) || peekPendingPageShareRestore(pageKey);
}

/**
 * Resolves true once this page's share payload has been applied.
 *
 * The page's own onMount must await this before writing defaults. Both onMounts start together,
 * and the defaults (TWS ALL, TACK, MAP, the viewer's saved filters and baseline) otherwise land
 * after the share and erase it.
 */
export function shareRestoreReady(pageKey: string): Promise<boolean> {
  return restoreByPage.get(pageKey) ?? Promise.resolve(false);
}

export function useDashboardShareRestore(
  pageKey: string,
  getPageState: () => Record<string, unknown>,
  applySharePayload: (payload: Record<string, unknown>) => boolean
): void {
  useDashboardPageShareMirror(pageKey, () => ({
    v: DASHBOARD_SHARE_VERSION,
    page: pageKey,
    ...getPageState(),
  }));

  let resolveReady: (applied: boolean) => void = () => {};
  const ready = new Promise<boolean>((resolve) => {
    resolveReady = resolve;
  });
  restoreByPage.set(pageKey, ready);

  onMount(() => {
    void (async () => {
      let applied = false;
      try {
        const pending = consumePendingPageShareRestore(pageKey);
        if (pending && applySharePayload(pending)) {
          applied = true;
          return;
        }
        const shareId = parseShareIdFromSearch();
        if (!shareId) {
          return;
        }
        const payload = await resolveDashboardShare(shareId);
        if (payload && applySharePayload(payload)) {
          applied = true;
          return;
        }
        warn(`[useDashboardShareRestore] share restore failed for ${pageKey}`);
      } catch (err) {
        warn(`[useDashboardShareRestore] share restore failed for ${pageKey}`, err);
      } finally {
        resolveReady(applied);
      }
    })();
  });
}
